import React, { useEffect, useState } from 'react';
import { familyNameService } from '../services/api';
import { Sidebar } from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { formatDateToDDMMYYYY } from '../utils/dateHelper';
import { FamilyNameFormModal } from '../components/FamilyNameFormModal';
import '../styles/users.scss';
import '../styles/layout.scss';

interface FamilyName {
  id: number;
  name: string;
  memberCount: number;
  createdAt: string;
  updatedAt?: string;
  headUserId?: number | null;
  headUserName?: string | null;
}

export const FamilyNamesPage: React.FC = () => {
  const [familyNames, setFamilyNames] = useState<FamilyName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedFamilyName, setSelectedFamilyName] = useState<FamilyName | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const { toggleSidebar } = useSidebar();

  useEffect(() => {
    loadFamilyNames();
  }, []);

  const loadFamilyNames = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await familyNameService.getAll();
      if (response.success) {
        setFamilyNames(response.data);
      } else {
        setError(response.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể tải danh sách dòng họ');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClick = () => {
    setSelectedFamilyName(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (familyName: FamilyName) => {
    setSelectedFamilyName(familyName);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await familyNameService.delete(id);
      setFamilyNames(familyNames.filter((item) => item.id !== id));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Không thể xóa dòng họ');
      setDeleteConfirm(null);
    }
  };

  const filteredFamilyNames = familyNames.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="main-content">
        <div className="header">
          <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <i className="fas fa-bars"></i>
          </button>
          <h1>
            <i className="fas fa-book"></i> Quản lý dòng họ
          </h1>
        </div>

        <div className="content-area">
          {error && <div className="alert alert-danger">{error}</div>}

          <div className="users-container">
            <div className="users-header">
              <h2>Dòng họ</h2>
              <button className="btn btn-primary" onClick={handleAddClick}>
                <i className="fas fa-plus"></i> Thêm dòng họ
              </button>
            </div>

            <div className="search-bar">
              <input
                type="text"
                placeholder="Tìm kiếm theo tên dòng họ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {isLoading ? (
              <div className="loading">
                <div className="spinner"></div>
              </div>
            ) : filteredFamilyNames.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>
                  {familyNames.length === 0
                    ? 'Chưa có dòng họ nào. Nhấp "Thêm dòng họ" để tạo.'
                    : 'Không tìm thấy dòng họ phù hợp với tìm kiếm.'}
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Tên dòng họ</th>
                        <th>Số thành viên</th>
                        <th>Trưởng họ</th>
                        <th>Ngày tạo</th>
                        <th>Hành động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFamilyNames.map((item) => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td>{item.name}</td>
                          <td>{item.memberCount}</td>
                          <td>{item.headUserName || 'Chưa chọn'}</td>
                          <td>
                            {formatDateToDDMMYYYY(item.createdAt)}
                          </td>
                          <td>
                            <div className="actions">
                              <button
                                className="action-btn edit-btn icon-only"
                                onClick={() => handleEditClick(item)}
                                title="Chỉnh sửa dòng họ"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="action-btn delete-btn icon-only"
                                onClick={() => setDeleteConfirm(item.id)}
                                title={
                                  item.memberCount > 0
                                    ? 'Không thể xóa dòng họ đang có thành viên'
                                    : 'Xóa dòng họ'
                                }
                                disabled={item.memberCount > 0}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>

                            {deleteConfirm === item.id && (
                              <div className="delete-confirm">
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  Xác nhận xóa
                                </button>
                                <button
                                  className="btn btn-sm btn-cancel"
                                  onClick={() => setDeleteConfirm(null)}
                                >
                                  Hủy
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mobile-card-list">
                  {filteredFamilyNames.map((item) => (
                    <div key={item.id} className="family-card">
                      <div className="card-header">
                        <div className="card-title">
                          <h3>{item.name}</h3>
                          <div className="card-meta">ID: {item.id}</div>
                        </div>
                      </div>
                      <div className="card-body">
                        <div className="card-info">
                          <span className="info-label">Số thành viên:</span>
                          <span className="info-value">{item.memberCount}</span>
                        </div>
                        <div className="card-info">
                          <span className="info-label">Trưởng họ:</span>
                          <span className="info-value">{item.headUserName || 'Chưa chọn'}</span>
                        </div>
                        <div className="card-info">
                          <span className="info-label">Ngày tạo:</span>
                          <span className="info-value">{formatDateToDDMMYYYY(item.createdAt)}</span>
                        </div>
                      </div>
                      <div className="card-footer">
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditClick(item)}
                          title="Chỉnh sửa dòng họ"
                        >
                          <i className="fas fa-edit"></i> Sửa
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => setDeleteConfirm(item.id)}
                          title={
                            item.memberCount > 0
                              ? 'Không thể xóa dòng họ đang có thành viên'
                              : 'Xóa dòng họ'
                          }
                          disabled={item.memberCount > 0}
                        >
                          <i className="fas fa-trash"></i> Xóa
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <FamilyNameFormModal
        familyName={selectedFamilyName}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedFamilyName(null);
        }}
        onSuccess={loadFamilyNames}
      />
    </div>
  );
};
